import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '../../../hooks/useUser';
import { useMyOrganizationQuery } from '../../../hooks/useDashboardQueries';
import { useDashboardLogout } from '../../../layouts/DashboardV2Layout/useDashboardLogout';
import { TelegramLoginCodesSection } from '../../../components/features/TelegramLoginCodesSection/TelegramLoginCodesSection';
import { IdWithCopyCell } from '../components/cells';
import { MobileBottomNav } from '../Stations/MobileBottomNav';
import './profile.css';

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="profile-detail-row">
    <dt className="profile-detail-label">{label}</dt>
    <dd className="profile-detail-value">{children}</dd>
  </div>
);

/**
 * The signed-in dashboard account: who they are, what they can do, and the
 * account-level actions (Telegram login codes, sign out). On mobile it is
 * also the only way out, since the header with its logout button is hidden.
 */
export const ProfileV2 = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const logout = useDashboardLogout();

  const organization = user?.organization ?? null;
  // /organizations/my answers for owners only; members just see their membership.
  const { data: ownedOrganization } = useMyOrganizationQuery(Boolean(organization?.isOwner));

  const identifier = user?.email || user?.phone || '';
  const notSet = t('dashboard.profile.notSet');
  const roles = user?.roles ?? [];

  const organizationLabel = () => {
    if (!organization) {
      return t('dashboard.profile.noOrganization');
    }
    if (!organization.isOwner) {
      return t('dashboard.profile.organizationMember');
    }
    return ownedOrganization?.name
      ? `${ownedOrganization.name} · ${t('dashboard.profile.organizationOwner')}`
      : t('dashboard.profile.organizationOwner');
  };

  const content = (
    <div className="profile-page">
      <section className="profile-card profile-summary">
        <div className="profile-avatar" aria-hidden="true">
          {identifier ? (
            identifier.replace(/^\+/, '').charAt(0).toUpperCase()
          ) : (
            <span className="material-symbols-outlined">person</span>
          )}
        </div>
        <div className="profile-summary-text">
          <div className="profile-identifier">{identifier || notSet}</div>
          {roles.length > 0 && (
            <ul className="profile-roles" aria-label={t('dashboard.profile.roles')}>
              {roles.map((role) => (
                <li key={role} className="profile-role-chip">
                  {role}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="profile-card">
        <h2 className="profile-card-title">{t('dashboard.profile.account')}</h2>
        <dl className="profile-details">
          <DetailRow label={t('dashboard.profile.email')}>{user?.email || notSet}</DetailRow>
          <DetailRow label={t('dashboard.profile.phone')}>{user?.phone || notSet}</DetailRow>
          <DetailRow label={t('dashboard.profile.organization')}>{organizationLabel()}</DetailRow>
          <DetailRow label={t('dashboard.profile.accountId')}>
            <IdWithCopyCell value={user?.id} copyLabel={t('dashboard.profile.copyAccountId')} emptyText={notSet} />
          </DetailRow>
        </dl>
      </section>

      <TelegramLoginCodesSection variant="light" />

      <button type="button" className="profile-logout-btn" onClick={logout}>
        <span className="material-symbols-outlined" aria-hidden="true">
          logout
        </span>
        {t('dashboard.profile.logout')}
      </button>
    </div>
  );

  return (
    <>
      <div className="desktop-layout">
        <div className="profile-desktop">{content}</div>
      </div>

      <div className="mobile-layout">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <h1 className="mobile-header-title">{t('dashboard.pageTitles.profile')}</h1>
          </div>
        </header>
        <main className="mobile-content">{content}</main>
        <MobileBottomNav />
      </div>
    </>
  );
};

export default ProfileV2;
